import { Device } from '@/store/Station/constants';
import { MetricType } from '@/constants/metric-type.enum';
import {
  getFirstPowerSensor,
  getRawPowerKw,
  getFirstFlowSensor,
  getPowerSensorKw,
  getRawFlowM3Min,
} from '@/comp/MapBuilder/nodes/compressor/CompressorNodeWidget.utils';
import rootStore from '@/store/RootStore';

export type MetricExtendedDevice = Device & {
  powerKw?: number;
  estimatedKw?: number;
  flowM3Min?: number;
  estimatedFlowM3Min?: number;
  connectivity?: string;
  [key: string]: unknown;
};

/**
 * Resolves metric values with special handling for metrics that require
 * onDevices lookups or complex calculation logic.
 *
 * This centralizes all special metric handling to avoid string comparisons
 * and ensure consistent logic across the application.
 */
export const resolveMetricValue = (
  device: MetricExtendedDevice,
  metricKey: MetricType,
  defaultValue: unknown,
): number | null => {
  const allDevices = rootStore.stationStore.currentStation?.devices;

  if (!allDevices) {
    return typeof defaultValue === 'number' ? defaultValue : null;
  }

  const onDevices = allDevices.filter(d => device.onDevices?.includes(d.deviceId));

  switch (metricKey) {
    case MetricType.PowerKw: {
      const currentStationId = rootStore.stationStore.currentStation?.id;
      const firstPowerSensor = getFirstPowerSensor(device, onDevices);
      const result = getRawPowerKw(device, currentStationId, firstPowerSensor?.powerKw);
      return typeof result === 'number' ? result : typeof defaultValue === 'number' ? defaultValue : null;
    }

    case MetricType.FlowM3Min: {
      const firstFlowSensor = getFirstFlowSensor(device, onDevices);
      const firstPowerSensor = getFirstPowerSensor(device, onDevices);
      const powerSensorKw = getPowerSensorKw(firstPowerSensor);
      const result = getRawFlowM3Min(device, firstFlowSensor, powerSensorKw);
      return typeof result === 'number' ? result : typeof defaultValue === 'number' ? defaultValue : null;
    }

    default:
      return typeof defaultValue === 'number' ? defaultValue : null;
  }
};

/**
 * Checks if a metric requires special handling (onDevices lookup, complex calculation, etc.)
 */
export const requiresSpecialHandling = (metricKey: MetricType): boolean => {
  return metricKey === MetricType.PowerKw || metricKey === MetricType.FlowM3Min;
};
